/* eslint-disable max-len */
import { DiscountType } from '@1po/1po-bff-fe-spec/generated/backoffice/promotion/model/Promotion';
import moment from 'moment';
import { isQueryReferenceNumber } from 'components/Page/Header/TopHeader/Search/Search';
import {
  AdditionalInformation,
  CFSectionLocal,
  PromotionTypeLocal,
  ReferencesSectionLocal,
  TargetAudienceLocal,
  TargetAudienceType,
  UploadedReferencesFromFile,
} from 'domains/promotion/Promotion.types';
import {
  containsAlreadyLocallyPromotedFamilyCodes,
  containsAlreadyLocallyPromotedReferences,
} from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PriceOptionStep/PromotionErrorNotifications';

export const isFilled = (s?: string): boolean => (s?.length ?? 0) > 0;

export const validateStepPromotionType = (promotionType: PromotionTypeLocal): boolean => {
  switch (promotionType) {
    case 'PROMOTION_FLASH':
      return false;
    default:
      return isFilled(promotionType);
  }
};

export const validateStepPromotionDetails = (
  publicationStart: string | undefined,
  publicationEnd: string | undefined,
  targetAudience: TargetAudienceLocal,
  targetAudienceType: TargetAudienceType,
  promotionType: PromotionTypeLocal,
  title: string | undefined,
  catchLine: string | undefined,
): boolean => {
  const startDate = moment(publicationStart);
  const endDate = moment(publicationEnd);

  const isValidStartEndDate = isFilled(publicationEnd) ? endDate >= startDate : true;
  const isValidDateRange = endDate.diff(startDate, 'd') <= 10;
  const isDateFilled = isFilled(publicationStart) && isFilled(publicationEnd) && endDate >= startDate;

  const isTargetFilled = () => {
    const { dealerTypes = [], dealers = [] } = targetAudience;
    if (targetAudienceType === 'NETWORK') {
      return dealerTypes.length > 0;
    } else {
      return dealers.length > 0;
    }
  };
  switch (promotionType) {
    case 'DISCOUNT':
      return isFilled(title) && isFilled(catchLine) && isTargetFilled() && isDateFilled;
    case 'PROMOTION_FLASH_TIME_LIMITED':
      return isFilled(title) && isFilled(catchLine) && isTargetFilled() && isDateFilled && isValidDateRange;
    case 'BANNER':
      return isFilled(title) && isFilled(catchLine) && isTargetFilled() && isDateFilled;
    case 'PROMOTION_FLASH_QUANTITY_LIMITED':
      return isFilled(title) && isTargetFilled() && isFilled(publicationStart) && isValidStartEndDate;
    default:
      return false;
  }
};

const validateReferenceFilled = (references: ReferencesSectionLocal[]) => {
  return references.length > 0 && references[0].items.length > 0;
};
const validateReferenceDiscountFilled = (references: ReferencesSectionLocal[]) => {
  return references.length > 0 && references[0].discount > 0;
};
const validateAnyDiscountFilled = (
  references: ReferencesSectionLocal[],
  uploadedReferencesFromFiles: UploadedReferencesFromFile[],
  commercialFamilyCodes: CFSectionLocal[],
) => {
  const isAnyReferenceDiscount = validateReferenceDiscountFilled(references);
  const isAnyFileDiscountFilled = uploadedReferencesFromFiles.some((f) => f.discount && f.discount > 0);
  const isAnyFamilyDiscountFilled = commercialFamilyCodes.some((c) => c.discount && c.discount > 0);
  return isAnyReferenceDiscount || isAnyFileDiscountFilled || isAnyFamilyDiscountFilled;
};
export const validateStepProduct = (
  references: ReferencesSectionLocal[],
  commercialFamilyCodes: CFSectionLocal[],
  uploadedReferencesFromFiles: UploadedReferencesFromFile[],
  duplicatePromotionIds: string[],
  additionalInformation: AdditionalInformation,
  promotionType: PromotionTypeLocal,
  discountType: DiscountType | '',
): boolean => {
  const isReferenceFilled = validateReferenceFilled(references);
  const isReferenceDiscountFilled = validateReferenceDiscountFilled(references);
  const isAnyDiscountFilled = validateAnyDiscountFilled(references, uploadedReferencesFromFiles, commercialFamilyCodes);

  const isProductSectionFilled =
    uploadedReferencesFromFiles.some((d) => d.rows && d.rows.length > 0) ||
    commercialFamilyCodes.some((section) => section.items.some((code) => code.item.length > 0)) ||
    references.some((section) => section.items.some((ref) => ref.item.length > 0));

  const areReferencesInValid = uploadedReferencesFromFiles.some((line) => {
    const isFileFilled = line.rows ? line.rows.length > 0 : false;
    const isDiscountFilled = line.discount > 0;
    return isFileFilled !== isDiscountFilled;
  });
  const areCFCodesInValid = commercialFamilyCodes.some((line) => {
    const isCFCodeFilled = line.items.length > 0;
    const isDiscountFilled = line.discount > 0;
    return isCFCodeFilled !== isDiscountFilled;
  });
  const areReferenceNumbersInValid = references.some((line) => {
    const isReferenceNumberFilled = line.items.length > 0;
    const isDiscountFilled = line.discount > 0;
    return isReferenceNumberFilled !== isDiscountFilled;
  });

  const duplicatePromotionsCheck = duplicatePromotionIds.length === 0;

  const containsLocallyPromotedCFCodes = containsAlreadyLocallyPromotedFamilyCodes(commercialFamilyCodes);
  const containsLocallyPromotedRefs = containsAlreadyLocallyPromotedReferences(references);
  const areProductsValid = !containsLocallyPromotedCFCodes && !containsLocallyPromotedRefs && duplicatePromotionsCheck;

  const isCorrectReferenceNumber = isQueryReferenceNumber(
    references && references.length > 0 && references[0].items.length > 0 ? references[0].items[0].item : '',
  );
  const isAdditionalFieldFilled = !!(
    additionalInformation.title ??
    additionalInformation.catchLine ??
    additionalInformation.description ??
    additionalInformation.image
  );
  const isAdditionalDiscountFilled = isAdditionalFieldFilled ? !!additionalInformation.discount : true;

  const collectDiscounts = [
    ...commercialFamilyCodes.map((c) => c.discount),
    ...uploadedReferencesFromFiles.map((f) => f.discount),
  ];
  const isOver100 = Math.max(...collectDiscounts) + (additionalInformation.discount ?? 0) > 100;

  switch (promotionType) {
    case 'DISCOUNT':
      return (
        isFilled(discountType) &&
        !(areCFCodesInValid || areReferencesInValid || areReferenceNumbersInValid) &&
        isProductSectionFilled &&
        areProductsValid &&
        isAdditionalDiscountFilled &&
        !isOver100
      );
    case 'PROMOTION_FLASH_TIME_LIMITED': {
      return isFilled(discountType) && isProductSectionFilled && isAnyDiscountFilled && areProductsValid;
    }
    case 'PROMOTION_FLASH_QUANTITY_LIMITED':
      return (
        isFilled(discountType) &&
        isReferenceFilled &&
        isReferenceDiscountFilled &&
        isCorrectReferenceNumber &&
        areProductsValid
      );
    case 'BANNER':
      return true;
    default:
      return false;
  }
};
